import { Box } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { OrganisationType } from "../../../../clients/graphqlTypes.js";
import { SignupLanguageSelector } from "../../../auth/signup/SignupLanguageSelector.js";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";
import { ONBOARDING_GUTTER } from "./onboardingDesign.js";
import {
  getOnboardingStepStage,
  getOnboardingSteps,
} from "./onboardingFlowSteps.js";
import { OnboardingStepSidebar } from "./OnboardingStepSidebar.js";
import {
  OnboardingStepLeaveContext,
  type OnboardingStepLeave,
} from "./onboardingStepLeave.js";

interface Props {
  stage: OnboardingFlowStage;
  organisationType: OrganisationType;
  /** Opens the stage of a step chosen in the rail (after the current stage let it go). */
  setStage: (newStage: OnboardingFlowStage | null) => void;
  children: React.ReactNode;
}

/**
 * The full-screen frame of the wizard: step sidebar left, the stage's content
 * right, with the language switch above it at the top right.
 */
export const OnboardingFlowShell = ({
  stage,
  organisationType,
  setStage,
  children,
}: Props) => {
  const { t } = useTranslate(["OrganisationProfile"]);
  // The current stage's handling of a jump from the rail, if it has one.
  const stepLeave = React.useRef<OnboardingStepLeave | null>(null);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <OnboardingStepSidebar
        heading={t("Set-up steps", { ns: "OrganisationProfile" })}
        steps={getOnboardingSteps(stage, organisationType)}
        onStepSelect={stepId => {
          const target = getOnboardingStepStage(stepId, organisationType);
          if (target === null || target === stage) {
            return;
          }
          if (stepLeave.current) {
            stepLeave.current(target);
          } else {
            setStage(target);
          }
        }}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* On desktop the language switch floats over the pane's top right, so
            the content is positioned as in the design (which has none). */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: ONBOARDING_GUTTER,
            pt: 2,
            pb: { xs: 1, md: 0 },
            position: { md: "absolute" },
            top: { md: 0 },
            right: { md: 0 },
            zIndex: 1,
          }}
        >
          <SignupLanguageSelector />
        </Box>
        <OnboardingStepLeaveContext.Provider value={stepLeave}>
          {children}
        </OnboardingStepLeaveContext.Provider>
      </Box>
    </Box>
  );
};
